import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import classNames from 'classnames';
import { ScrapbookContentType, type AnalyticsBuilderReportSectionData } from '@/types';
import CopyIcon from '@/assets/copy-simple-light.svg';
import { SaveToScrapbookButton } from '../scrapbook';

type AnalyticsBuilderReportSectionProps = {
  section: AnalyticsBuilderReportSectionData;
  index: number;
  hideCopyToClipboardButton?: boolean;
  hideSaveToScrapbookButton?: boolean;
  reportTitle?: string;
  reportDescription?: string;
  reportDisplayName?: string;
};

const getSectionHtml = (section: AnalyticsBuilderReportSectionData) => `
  <html>
    <body>
      <h2 style="font-family: Arial, Helvetica, sans-serif; font-size: 24pt; line-height: 1.25; font-weight: 700; color: #09090b; margin: 0 0 12pt;">
        ${section.title}
      </h2>
      ${section.html || ''}
    </body>
  </html>
`;

const getSectionPlainText = (section: AnalyticsBuilderReportSectionData) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = getSectionHtml(section);
  return tempDiv.innerText || tempDiv.textContent || '';
};

export const handleCopyToClipboard = async (section: AnalyticsBuilderReportSectionData) => {
  const formattedHtmlContent = getSectionHtml(section);
  const formattedContent = getSectionPlainText(section);

  if (navigator.clipboard?.write) {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([formattedHtmlContent], { type: 'text/html' }),
        'text/plain': new Blob([formattedContent], { type: 'text/plain' }),
      }),
    ]);
  } else if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(formattedContent);
  }
};

const AnalyticsBuilderReportSection = ({ section, index, hideCopyToClipboardButton, hideSaveToScrapbookButton, reportTitle, reportDescription, reportDisplayName }: AnalyticsBuilderReportSectionProps) => {
  const [isSourcesExpanded, setIsSourcesExpanded] = useState(false);
  const [showCopied, setShowCopied] = useState(false);
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrapbookPathRef = useRef(`analyticsBuilderSection.${Date.now()}.json`);

  useEffect(
    () => () => {
      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current);
      }
    },
    [],
  );

  const handleSectionCopyToClipboard = async () => {
    try {
      await handleCopyToClipboard(section);
      setShowCopied(true);

      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current);
      }

      copiedTimeoutRef.current = setTimeout(() => {
        setShowCopied(false);
      }, 2000);
    } catch {
      setShowCopied(false);
    }
  };

  return (
    <section id={section.id} className="overflow-hidden rounded-sm border border-zinc-200 bg-white shadow-sm">
      <div className="border-b border-zinc-100 bg-zinc-50 px-8 py-5">
        <div className="flex items-center gap-3">
          <span className="flex h-7 w-7 items-center justify-center rounded-[6px] bg-white text-xs font-semibold text-zinc-500 ring-1 ring-zinc-200">
            {index + 1}
          </span>
          <h2 className="text-2xl font-semibold text-zinc-950">{section.title}</h2>
          <div className="ml-auto flex items-center gap-2">
            {!hideCopyToClipboardButton && (
              <div className="relative">
                {showCopied && (
                  <div className="absolute top-0 -left-18 flex w-full justify-center text-center text-sm font-medium text-black">
                    <div className="w-auto rounded-md  p-2">Copied...</div>
                  </div>
                )}
                <button type="button" className="btn icon" onClick={handleSectionCopyToClipboard}>
                  <CopyIcon className="mt-0.5 h-5 w-5 !fill-black" />
                </button>
              </div>
            )}
            {!hideSaveToScrapbookButton && (
              <SaveToScrapbookButton
                scrapbookContent={{
                  ...section,
                  reportTitle: reportTitle,
                  reportDescription: reportDescription,
                  reportDisplayName: reportDisplayName,
                }}
                scrapbookContentType={ScrapbookContentType.AnalyticsBuilderSection}
                scrapbookPath={scrapbookPathRef.current}
                successMessage="Analytics Builder content was added to scrapbook successfully"
                tooltipProps={{ inactive: true, content: ' ' }}

              />
            )}


          </div>


        </div>
      </div>
      <div className="p-8">
        <div className="space-y-5">
          {section.html && (
            <div
              className={classNames(
                'richText space-y-5 leading-7 text-zinc-700',
                '[&_h1]:!text-5xl [&_h2]:!text-4xl [&_h3]:!text-3xl [&_h4]:!text-2xl [&_h5]:!text-xl [&_h6]:!text-lg',
                '[&_h1]:!leading-[3.5rem] [&_h2]:!leading-[3rem] [&_h3]:!leading-[2.5rem] [&_h4]:!leading-[2rem] [&_h5]:!leading-[1.5rem] [&_h6]:!leading-[1.25rem]',
                'overflow-x-auto [&_table]:w-full [&_table]:border-collapse [&_table]:text-left [&_table]:text-sm',
                '[&_th]:border [&_th]:border-zinc-200 [&_th]:bg-zinc-50 [&_th]:px-4 [&_th]:py-3 [&_th]:font-semibold [&_th]:text-zinc-900',
                '[&_td]:border [&_td]:border-zinc-200 [&_td]:px-4 [&_td]:py-3 [&_td]:align-top',
              )}
              dangerouslySetInnerHTML={{ __html: section.html }}
            />
          )}

          {section.sources && section.sources.length > 0 && (
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => setIsSourcesExpanded((current) => !current)}
                className="btn !text-sm !font-medium text-zinc-600 underline"
                aria-expanded={isSourcesExpanded}
              >
                {isSourcesExpanded ? 'Hide sources' : `Show sources (${section.sources.length})`}
              </button>

              <AnimatePresence initial={false}>
                {isSourcesExpanded && (
                  <motion.div
                    className="overflow-hidden"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.22, ease: 'easeInOut' }}
                  >
                    <div className="space-y-3 text-sm text-zinc-600">
                      {section.sources.map((source, sourceIndex) => (
                        <div
                          key={`${source.sourceId}-${sourceIndex}`}
                          className="rounded-[6px] border border-zinc-200 bg-white px-4 py-3"
                        >
                          <p className="text-md font-semibold">
                            [{sourceIndex + 1}] {source.title}
                          </p>
                          {source.chunkText && <p className="mt-1 text-sm text-zinc-600">{source.chunkText}</p>}
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default AnalyticsBuilderReportSection;
